import { ref } from "@vue/composition-api";
import {
  DATE_FORMATS,
  DATE_WITH_TIME_FORMATS,
  validateDateFormat,
  getBusinessDate
} from "../date-helper";
import { isNumber, isString } from "../type-helper";
import dayjs from "dayjs";

type Config = {
  required?: boolean;
  isNumber?: boolean;
  isEmail?: boolean;
  isUrl?: boolean;
  /** YYYY/MM/DD 形式 */
  isDate?: boolean;
  /** YYYY/MM/DD HH:mm 形式 */
  isDateAndTime?: boolean;
  /** 最小文字数 */
  minLength?: number;
  /** 最大文字数 */
  maxLength?: number;
  /** 最小数値(isNumberと併用) */
  minCount?: number;
  /** 最大数値(isNumberと併用) */
  maxCount?: number;
  /** 重複を許可しない値 */
  uniqueValues?: (string | number | boolean)[];
  /** n営業日以降 */
  businessDaysLater?: number;
  /** カスタムバリデーション */
  customErrors?: Error<any>[];
};

export type Error<T = keyof Config | "isDateRange", M = any> = {
  message: string;
  meta?: M;
  type?: T;
};

export type FormErrors = { [key: string]: Error[] };

const getErrorMessagePrefix = (subject: string | undefined, particle: string) =>
  subject ? subject + particle : "";

export const useValidate = () => {
  const formErrors = ref<FormErrors>({});

  const setFormErrors = (errors: Error[], key: string) => {
    const nextFormErrors = { ...formErrors.value };

    if (errors.length) {
      nextFormErrors[key] = errors;
    } else {
      delete nextFormErrors[key];
    }

    formErrors.value = nextFormErrors;
  };

  const resetErrorMessage = (key: string) => {
    const nextFormErrors = { ...formErrors.value };
    delete nextFormErrors[key];

    formErrors.value = nextFormErrors;
  };

  const getFirstErrorMessage = (key: string) => {
    return formErrors.value[key]?.[0]?.message;
  };

  const textValidate = (
    value: string,
    config: Config,
    subject?: string
  ): Error[] => {
    let errors: Error[] = [];

    if (config.minLength !== undefined) {
      const validated = value.length >= config.minLength;
      if (!validated) {
        errors.push({
          type: "minLength",
          message:
            getErrorMessagePrefix(subject, "には") +
            `${config.minLength}文字以上入力してください`
        });
      }
    }

    if (config.maxLength !== undefined) {
      const validated = value.length <= config.maxLength;
      if (!validated) {
        errors.push({
          type: "maxLength",
          message:
            getErrorMessagePrefix(subject, "には") +
            `${config.maxLength}文字以下で入力してください`
        });
      }
    }

    if (config.isEmail) {
      const pattern = new RegExp(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
      const validated = pattern.test(value);
      if (!validated) {
        errors.push({
          type: "isEmail",
          message:
            getErrorMessagePrefix(subject, "に") +
            `正しいメールアドレスを入力してください`
        });
      }
    }

    if (config.isUrl) {
      const pattern = new RegExp(
        "^(https?:\\/\\/)" + // protocol
        "((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|" + // domain name
        "((\\d{1,3}\\.){3}\\d{1,3}))" + // OR ip (v4) address
        "(\\:\\d+)?(\\/[-a-z\\d%_.~+@]*)*" + // port and path
        "(\\?[;&a-z\\d%_.~+=-]*)?" + // query string
          "(\\#[-a-z\\d_]*)?$",
        "i"
      ); // fragment locator

      const validated = pattern.test(value);
      if (!validated) {
        errors.push({
          type: "isUrl",
          message:
            getErrorMessagePrefix(subject, "に") + `正しいURLを入力してください`
        });
      }
    }

    if (config.isDate) {
      const validated = validateDateFormat(value, DATE_FORMATS);
      if (!validated) {
        errors.push({
          type: "isDate",
          message:
            getErrorMessagePrefix(subject, "に") +
            "有効な日付を設定してください（例: 2022年01月01日）"
        });
      }
    }

    if (config.isDateAndTime) {
      const validated = validateDateFormat(value, DATE_WITH_TIME_FORMATS);
      if (!validated) {
        errors.push({
          type: "isDate",
          message:
            getErrorMessagePrefix(subject, "に") +
            "有効な日時を設定してください（例: 2022年01月01日 12時00分）"
        });
      }
    }

    if (config.businessDaysLater !== undefined) {
      const minimumDate = getBusinessDate(config.businessDaysLater + 1);
      const validated =
        dayjs(minimumDate).isSame(new Date(value), "date") ||
        dayjs(minimumDate).isBefore(new Date(value), "date");
      if (!validated) {
        errors.push({
          type: "businessDaysLater",
          message:
            getErrorMessagePrefix(subject, "は") +
            `${config.businessDaysLater}営業日より先にしてください`
        });
      }
    }

    return errors;
  };

  const numberValidate = (
    value: number,
    config: Config,
    subject?: string
  ): Error[] => {
    let errors: Error[] = [];

    if (config.minCount !== undefined) {
      const validated = value >= config.minCount;
      if (!validated) {
        errors.push({
          type: "minCount",
          message:
            getErrorMessagePrefix(subject, "には") +
            `${config.minCount}以上の数値を入力してください`
        });
      }
    }

    if (config.maxCount !== undefined) {
      const validated = value <= config.maxCount;
      if (!validated) {
        errors.push({
          type: "maxCount",
          message:
            getErrorMessagePrefix(subject, "には") +
            `${config.maxCount}以下の数値を入力してください`
        });
      }
    }

    return errors;
  };

  /**
   * プリミティブな値のバリデーション
   */
  const validate = (
    value: string | number | boolean | undefined,
    config: Config,
    formErrorsKey?: string,
    subject?: string
  ) => {
    let errors: Error[] = [];

    if (config.required) {
      const validated = !!value || value === 0 || value === false;
      if (!validated) {
        errors.push({
          type: "required",
          message: getErrorMessagePrefix(subject, "を") + "入力してください"
        });
      }
    }

    if (value && config.uniqueValues?.length) {
      const validated = !config.uniqueValues.includes(value);
      if (!validated) {
        errors.push({
          type: "uniqueValues",
          message:
            getErrorMessagePrefix(subject, "の") + "入力内容が重複しています"
        });
      }
    }

    if (config.isNumber && (value || value === 0)) {
      const validated = isNumber(value);
      if (!validated) {
        errors.push({
          type: "isNumber",
          message:
            getErrorMessagePrefix(subject, "に") + "数値を入力してください"
        });
      } else {
        const numberErrors = numberValidate(value as number, config, subject);
        errors.push(...numberErrors);
      }
    }

    if (!config.isNumber && value) {
      const validated = isString(value);
      if (validated) {
        const textErrors = textValidate(value as string, config, subject);
        errors.push(...textErrors);
      }
    }

    if (config.customErrors?.length) {
      config.customErrors.forEach(customError => {
        errors.push(customError);
      });
    }

    if (formErrorsKey) {
      setFormErrors(errors, formErrorsKey);
    }

    return errors;
  };

  /**
   * 日付期間のバリデーション
   */
  const validateDateRange = (
    values: [string, string],
    formErrorsKey?: string,
    subject?: string
  ) => {
    let errors: Error[] = [];

    const validatedRequired = values.every(value => !!value);
    if (!validatedRequired) {
      errors.push({
        type: "required",
        message: getErrorMessagePrefix(subject, "を") + "入力してください"
      });
    }

    const validatedDateSeries = values[0] <= values[1];
    if (!validatedDateSeries) {
      errors.push({
        type: "isDateRange",
        message:
          getErrorMessagePrefix(subject, "に") +
          "正しい時系列を入力してください"
      });
    }

    if (formErrorsKey) {
      setFormErrors(errors, formErrorsKey);
    }

    return errors;
  };

  return {
    formErrors,
    resetErrorMessage,
    getFirstErrorMessage,
    validate,
    validateDateRange
  };
};
